import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAnalystBench,
  AnalystBenchProvider,
  findInstitutionById,
  type NavSection,
} from '../../context/AnalystBenchContext';
import NavigationPanel from '../../components/analyst-bench/NavigationPanel';
import ContentPanel from '../../components/analyst-bench/ContentPanel';
import BenchHeader from '../../components/analyst-bench/BenchHeader';
import BenchHome from './BenchHome';
import './AnalystBench.css';
import './BenchHome.css';

function AnalystBenchWorkspace() {
  const [leftPanelWidth, setLeftPanelWidth] = useState(260);

  return (
    <div className="bench-workspace">
      {/* Left Navigation Panel */}
      <div
        className="bench-panel bench-nav-panel"
        style={{ width: leftPanelWidth }}
      >
        <NavigationPanel />
      </div>

      {/* Resize handle */}
      <div
        className="panel-resize-handle"
        onMouseDown={(e) => {
          e.preventDefault();
          const startX = e.clientX;
          const startWidth = leftPanelWidth;

          const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = startWidth + (moveEvent.clientX - startX);
            setLeftPanelWidth(Math.max(220, Math.min(400, newWidth)));
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }}
      />

      {/* Main Content Panel - Full Width */}
      <div className="bench-panel bench-content-panel">
        <ContentPanel />
      </div>
    </div>
  );
}

function AnalystBenchContent() {
  // Support both new format (/analyst-bench/:institutionId) and legacy format (/analyst-bench/:institutionId/:period)
  const { institutionId, period } = useParams<{ institutionId?: string; period?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    selectedInstitution,
    setSelectedInstitution,
    setActiveSection,
    setReportsPeriod,
  } = useAnalystBench();

  // Sync URL params to state
  useEffect(() => {
    if (institutionId) {
      // URL has institution - sync to state
      const institution = findInstitutionById(institutionId);
      if (institution && (!selectedInstitution || selectedInstitution.id !== institutionId)) {
        setSelectedInstitution(institution);
      }

      // If using legacy URL with period, redirect to new format
      if (period) {
        navigate(`/analyst-bench/${institutionId}`, { replace: true });
      }

      // Handle query params for tab and period (from BenchHome clicks)
      const tabParam = searchParams.get('tab');
      const periodParam = searchParams.get('period');

      if (tabParam === 'overview') {
        setActiveSection('landing' as NavSection);
        // Clear the query params after applying
        setSearchParams({}, { replace: true });
      } else if (tabParam === 'reports') {
        setActiveSection('reports' as NavSection);
        // Clear the query params after applying
        setSearchParams({}, { replace: true });
      }

      if (periodParam) {
        // Set the period for the reports view
        setReportsPeriod(periodParam);
      }
    } else {
      // URL is home - clear state if needed
      if (selectedInstitution) {
        setSelectedInstitution(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, period, searchParams]);

  const isHome = !institutionId;
  const hasSelection = !!selectedInstitution;

  return (
    <div className="analyst-bench">
      <BenchHeader />

      {isHome ? (
        <BenchHome />
      ) : !hasSelection ? (
        <div className="bench-empty-state">
          <div className="empty-state-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="20" x2="12" y2="10"/>
              <line x1="18" y1="20" x2="18" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="16"/>
            </svg>
          </div>
          <h3>{t('bench.selectInstitution')}</h3>
          <p>{t('bench.selectInstitutionHint')}</p>
        </div>
      ) : (
        <AnalystBenchWorkspace />
      )}
    </div>
  );
}

export default function AnalystBench() {
  return (
    <AnalystBenchProvider>
      <AnalystBenchContent />
    </AnalystBenchProvider>
  );
}
